import { LoaderCircle, Radio, X } from "lucide-react";
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from "react";
import { createPortal } from "react-dom";
import { usePresence } from "./realtime";
import { parseChatDate } from "./chatTime";
export { formatChatDateTime, formatChatTime, messageDayKey, messageDayLabel, parseChatDate } from "./chatTime";

export function SpaceBackground({ children, className = "" }: { children: ReactNode; className?: string }) {
  return <div className={`space-background ${className}`.trim()}><div className="star-layer" />{children}</div>;
}

export function Avatar({
  user,
  size = "md",
  online
}: {
  user?: { id?: string; displayName?: string; username?: string; avatarThumbUrl?: string; avatarUrl?: string; online?: boolean; aura?: { tier?: number; activityTier?: number | null; activityBadgeUrl?: string | null; foundingBadgeTier?: number; foundingBadgeUrl?: string | null; activeDays?: number; conversationDays?: number; foundingMember?: boolean } } | null;
  size?: "sm" | "md" | "lg";
  online?: boolean;
}) {
  const name = user?.displayName || user?.username || "?";
  const rawSrc = user?.avatarThumbUrl || user?.avatarUrl;
  const src = rawSrc && rawSrc.startsWith("/") ? `${window.location.origin}${rawSrc}` : rawSrc;
  const presence = usePresence(user?.id);
  const isOnline = presence ? presence.online : (online ?? user?.online);
  const activeDays = Number(user?.aura?.activeDays || 0);
  const conversationDays = Number(user?.aura?.conversationDays || 0);
  const measuredActivityTier = activeDays >= 6 && conversationDays >= 6 ? 4
    : activeDays >= 4 && conversationDays >= 4 ? 3
      : activeDays > 0 && conversationDays > 0 ? 2
        : activeDays > 0 ? 1
          : 0;
  const legacyActivityTier = user?.aura?.foundingMember ? 0 : Number(user?.aura?.tier || 0);
  const activityTier = Math.max(0, Math.min(4, Number(user?.aura?.activityTier ?? (measuredActivityTier || legacyActivityTier))));
  const hasFounderBadge = Boolean(user?.aura?.foundingMember || user?.aura?.foundingBadgeTier === 5);
  const activityBadgeUrl = user?.aura?.activityBadgeUrl || (activityTier ? `/aura-badges/aura${activityTier}.webp` : null);
  const foundingBadgeUrl = user?.aura?.foundingBadgeUrl || (hasFounderBadge ? "/aura-badges/aura5.png" : null);
  const showAuraBadges = size === "lg" && Boolean(activityBadgeUrl || foundingBadgeUrl);
  return (
    <span className={`avatar avatar-${size}${activityTier ? ` avatar-aura avatar-aura-tier-${activityTier}` : ""}${showAuraBadges ? " avatar-aura-stack" : ""}`}>
      <span className="avatar-visual">
        <span className="avatar-core">
          {src ? <img className="avatar-photo" src={src} alt="" /> : <span className="avatar-fallback">{name.slice(0, 1).toUpperCase()}</span>}
        </span>
        {activityTier ? <img className="avatar-aura-fire" src="/aura-badges/aura-fire.gif" alt="" aria-hidden="true" /> : null}
        {isOnline ? <i className="online-dot" aria-label="Online" /> : null}
      </span>
      {hasFounderBadge && size !== "lg" && foundingBadgeUrl ? <img className="avatar-founder-mark" src={foundingBadgeUrl} alt="Founder" title="Founder" /> : null}
      {showAuraBadges ? (
        <span className="avatar-aura-badges" aria-label={`${hasFounderBadge ? "Founder Aura 5" : ""}${hasFounderBadge && activityTier ? ", " : ""}${activityTier ? `Activity Aura ${activityTier}` : ""}`}>
          {activityBadgeUrl ? <img className="avatar-aura-badge avatar-aura-badge-activity" src={activityBadgeUrl} alt={`Activity Aura ${activityTier}`} /> : null}
          {foundingBadgeUrl ? <img className="avatar-aura-badge avatar-aura-badge-founder" src={foundingBadgeUrl} alt="Founder Aura 5" /> : null}
        </span>
      ) : null}
    </span>
  );
}

export function Panel({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement> & { children: ReactNode }) {
  return <div className={`panel ${className}`} {...props}>{children}</div>;
}

export function StatusPill({ children, tone = "green" }: { children: ReactNode; tone?: "green" | "cyan" | "amber" | "red" }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

export function CommandHeader({
  icon,
  title,
  subtitle,
  action
}: {
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="command-header">
      {icon ? <span className="command-icon">{icon}</span> : null}
      <div className="command-copy">
        <h1>{title}</h1>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {action ? <div className="command-action">{action}</div> : null}
    </header>
  );
}

export function IconButton({
  label,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return <button type="button" className={`icon-button ${className}`} title={label} aria-label={label} {...props} />;
}

export function EmptyState({ title, detail }: { title: string; detail?: string }) {
  return (
    <div className="empty-state">
      <Radio size={32} />
      <h2>{title}</h2>
      {detail ? <p>{detail}</p> : null}
    </div>
  );
}

export function LoadingState({ label = "Connecting to comm net..." }: { label?: string }) {
  return <div className="loading-state"><LoaderCircle className="spin" size={22} /> {label}</div>;
}

export function Modal({
  title,
  children,
  onClose,
  wide = false,
  fullScreen = false,
  closeable = true,
  pullUp = false
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  fullScreen?: boolean;
  closeable?: boolean;
  pullUp?: boolean;
}) {
  return createPortal((
    <div className={`modal-backdrop${pullUp ? " modal-backdrop-pull-up" : ""}`} role="presentation" onMouseDown={() => { if (closeable) onClose(); }}>
      <section className={`modal ${wide ? "modal-wide" : ""} ${fullScreen ? "modal-full-screen" : ""}${pullUp ? " modal-pull-up" : ""}`} role="dialog" aria-modal="true" aria-label={title} onMouseDown={event => event.stopPropagation()}>
        <header><h2>{title}</h2>{closeable ? <IconButton label="Close" onClick={onClose}><X size={20} /></IconButton> : null}</header>
        {children}
      </section>
    </div>
  ), document.body);
}

export function timeAgo(value?: string) {
  if (!value) return "";
  const date = parseChatDate(value);
  if (!date) return "";
  const time = date.getTime();
  const delta = Math.max(0, Date.now() - time);
  if (delta < 60_000) return "now";
  if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m`;
  if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h`;
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong";
}
